/**
 * Shapes the API actually returns, transcribed from the Rust that serializes them —
 * `lrd_core::schema`, `lrd_server::pipeline` and the handlers in `http.rs`. Not
 * inferred from field names: `PageContent.index` is not `page`, region confidence is
 * optional, and money is `Option<i64>` cents where `null` means unknown.
 */

/**
 * `stale` now means what it says: a job finished and yet nothing was stored for this
 * document at *any* version. It is rare and genuinely wrong.
 *
 * It used to also cover the ordinary case of a document read by an older build, which made
 * every version bump strand the whole corpus. That case is `succeeded` with `current:
 * false` — the content is served and simply labelled with the build that produced it.
 */
export type DocumentStatus =
  | "queued"
  | "processing"
  | "succeeded"
  | "stale"
  | "failed"
  | "unknown";

/**
 * Which view can show what a document produced. `rendered` is the only mode with a printed
 * page and the only one carrying region geometry; the rest are views over the block
 * structure the parse recovered, which for those formats is exact rather than inferred.
 */
export type PreviewMode = "rendered" | "document" | "sheet" | "slides" | "none";

export interface Preview {
  mode: PreviewMode;
  /** Present only for `none`, and written for a person to act on. */
  reason?: string | null;
}

/**
 * `lrd_core::document::Grid`. Labels and row numbers are computed in Rust and shipped as
 * text: the console never derives a column letter from an index, because that derivation
 * is only correct with the used-range offset the console does not have. A dropped blank or
 * malformed row leaves a gap in `rows` rather than shifting what follows it.
 */
export interface Grid {
  columns: string[];
  header_row: number | null;
  rows: number[];
}

/** `lrd_core::document::Block`, tagged by `kind`. */
export type StructureBlock =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list_item"; text: string; depth: number }
  | { kind: "caption"; text: string }
  | { kind: "aside"; text: string; label?: string | null }
  | { kind: "image"; alt?: string | null; bytes_key?: string | null }
  | {
      kind: "table";
      header: string[];
      rows: string[][];
      has_header: boolean;
      grid?: Grid | null;
    };

/** Where a block came from: which sheet, slide or chapter, and in what order. */
export interface Origin {
  unit: number;
  unit_label?: string | null;
  order: number;
}

export interface PlacedBlock {
  block: StructureBlock;
  origin: Origin;
}

/** `GET /v1/documents/{id}/structure`. */
export interface DocumentStructure {
  blocks: PlacedBlock[];
  incomplete?: string[];
}

/** `lrd_core::schema::Method`, snake_case. */
export type Method = "text_layer" | "ocr" | "vlm" | "profile_derived" | "human";

/** `lrd_core::schema::Severity`, snake_case. */
export type Severity = "high" | "medium" | "low";

export type Status = "open" | "closed" | "reopened";

export interface Progress {
  stage: string;
  pages_done: number | null;
  pages_total: number | null;
}

export interface DocumentSummary {
  id: string;
  sha256: string;
  byte_size: number;
  filename: string | null;
  content_type: string | null;
  doc_type: string | null;
  carrier: string | null;
  created_at: string | null;
  status: DocumentStatus;
  /** The build that produced the served run; `null` when nothing has parsed it yet. */
  run_parser_version?: string | null;
  /** False when the served run came from an older build. The content is still shown. */
  current?: boolean;
  /** Which view can show this document. Absent on the list endpoint. */
  preview?: Preview;
  parse_run_id: string | null;
  /** Always present; empty when the document is in no collection. */
  collections: CollectionRef[];
  /** Always present; empty when the document has no tag. */
  tags: TagRef[];
}

export interface DocumentList {
  documents: DocumentSummary[];
  next_cursor: string | null;
  parser_version: string;
}

export interface JobRef {
  id: string;
  state: string;
  attempts: number;
  last_error: string | null;
}

/** `GET /v1/documents/{id}` — no `created_at`; carries the job and live progress. */
export interface DocumentDetail {
  id: string;
  sha256: string;
  byte_size: number;
  filename: string | null;
  content_type: string | null;
  carrier: string | null;
  status: DocumentStatus;
  /** The build that produced the served run; `null` when nothing has parsed it yet. */
  run_parser_version?: string | null;
  /** False when the served run came from an older build. The content is still shown. */
  current?: boolean;
  /** Which view can show this document. Absent on the list endpoint. */
  preview?: Preview;
  parse_run_id: string | null;
  parser_version: string;
  tags: TagRef[];
  collections: CollectionRef[];
  job: JobRef | null;
  progress: Progress | null;
}

export interface Job {
  id: string;
  kind: string;
  state: string;
  attempts: number;
  max_attempts?: number;
  last_error: string | null;
  document_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  progress?: Progress | null;
}

export interface JobList {
  jobs: Job[];
  next_cursor?: string | null;
}

/** `RegionContent`: pixels of the corrected page frame, `[left, top, right, bottom]`. */
export interface Region {
  text: string;
  confidence?: number;
  bbox: [number, number, number, number];
}

export interface Checkbox {
  bbox: [number, number, number, number];
  checked: boolean;
  fill: number;
  label?: string;
}

/** `PageContent`. Zero-based `index`; empty collections are omitted, not null. */
export interface Page {
  index: number;
  method: Method;
  markdown: string;
  /**
   * Size of the frame `regions` and `checkboxes` are measured in. Absent for a page
   * read before the server recorded it — the overlay then has no true frame and falls
   * back to the extent of the ink, which is approximate.
   */
  width?: number;
  height?: number;
  scripts?: string[];
  low_confidence?: string[];
  regions?: Region[];
  checkboxes?: Checkbox[];
}

export interface KvPair {
  key: string;
  value: string;
}

export interface ContentResponse {
  document_id: string;
  parser_version: string;
  content: { pages: Page[]; kv: KvPair[] };
  fields: Record<string, unknown>;
}

/** `GET /v1/documents/{id}/exceptions` — open flags, worst first. No `id` field. */
export interface Exception {
  rule: string;
  severity: Severity;
  fields: string[];
  message: string;
  row_id: string | null;
  claim_number: string | null;
  row_index: number | null;
  incurred_cents: number | null;
  page: number | null;
}

export interface ExceptionList {
  exceptions: Exception[];
}

export interface Provenance {
  page: number;
  method: Method;
  bbox?: [number, number, number, number];
  model?: string;
}

export interface RowFlag {
  rule: string;
  severity: Severity;
  fields: string[];
  message: string;
  row_index?: number;
}

/** `lrd_core::schema::LossRow`, plus the `id`/`row_index` the handler grafts on. */
export interface LossRow {
  id?: string;
  row_index?: number;
  claim_number: string;
  policy_number?: string;
  policy_year: number;
  date_of_loss: string;
  date_reported?: string;
  status: Status;
  cause?: string;
  paid_indemnity_cents: number | null;
  paid_expense_cents: number | null;
  reserve_indemnity_cents: number | null;
  reserve_expense_cents: number | null;
  incurred_cents: number | null;
  litigation?: boolean;
  source: Provenance;
  flags: RowFlag[];
}

export interface RowList {
  rows: LossRow[];
  next_cursor: number | null;
  total: number;
}

export interface Webhook {
  id: string;
  url: string;
  created_at: string;
}

export interface WebhookList {
  webhooks: Webhook[];
}

/** What the server did with a correction — including the canonical form it stored. */
export interface Applied {
  correction_id: string;
  field: string;
  old_value: string | null;
  new_value: string;
  /** Cents for money, ISO for dates: the form used for scoring. */
  normalized: string;
}

export interface Label {
  field: string;
  old_value: string | null;
  new_value: string;
  page: number | null;
  carrier?: string | null;
  parser_version?: string | null;
  document_sha256?: string | null;
}

export interface LabelList {
  labels: Label[];
}

/**
 * Fields a human may correct. Mirrors `lrd_server::review::CORRECTABLE` — the server
 * rejects anything else, so this list only decides which fields offer an edit control.
 */
export const CORRECTABLE = [
  "claim_number",
  "date_of_loss",
  "date_reported",
  "status",
  "paid_indemnity_cents",
  "paid_expense_cents",
  "reserve_indemnity_cents",
  "incurred_cents",
] as const;

export type CorrectableField = (typeof CORRECTABLE)[number];

export function isCorrectable(field: string): field is CorrectableField {
  return (CORRECTABLE as readonly string[]).includes(field);
}

/** One hit of `GET /v1/search`. `snippet` carries `<b>…</b>` around matched terms. */
export interface SearchHit {
  document_id: string;
  filename: string | null;
  snippet: string;
  rank: number;
}

/** `GET /v1/search?q=&limit=`. */
export interface SearchResponse {
  query: string;
  results: SearchHit[];
}

export type Role = "owner" | "admin" | "member" | "viewer";

export interface Limits {
  /** Cap on one request body in bytes — the sum of a batch, not per file. */
  max_upload_bytes: number;
  max_batch_files: number;
  /** Cap on ids in one bulk call. Smaller than `max_batch_files`, and not the same job. */
  max_bulk_ids: number;
}

export interface Me {
  tenant_id: string;
  tenant_name: string;
  actor: { kind: "user" | "key"; id: string };
  email: string | null;
  display_name: string | null;
  role: Role;
  permissions: string[];
  /** Absent on an older server; the uploader falls back to conservative defaults. */
  limits?: Limits;
}

export interface SessionResponse {
  user_id: string;
  tenant_id: string;
  role: Role;
  display_name: string;
  email: string;
  expires_at: string;
}

export interface UserSummary {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  created_at: string | null;
  disabled_at: string | null;
}

export interface InviteSummary {
  id: string;
  email: string;
  role: Role;
  created_at: string;
  expires_at: string;
  created_by: string;
}

export interface UsersResponse {
  users: UserSummary[];
  invites: InviteSummary[];
}

export interface NewInviteResponse {
  id: string;
  email: string;
  role: Role;
  token: string;
  invite_path: string;
  expires_at: string;
}

export interface KeySummary {
  id: string;
  label: string;
  role: Role;
  created_at: string;
  revoked_at: string | null;
}

export interface KeysResponse {
  keys: KeySummary[];
}

export interface NewKeyResponse {
  id: string;
  label: string;
  role: Role;
  key: string;
}

export interface AuditEntry {
  at: string;
  actor: string;
  action: string;
  subject_type: string | null;
  subject_id: string | null;
  detail: unknown;
  source_ip: string | null;
}

export interface AuditPage {
  events: AuditEntry[];
  next_cursor: string | null;
}

export interface ErasureReceipt {
  erasure_id: string;
  documents: number;
  rows_erased: number;
  blobs_listed: number;
  completed_at: string;
}

export interface RetentionState {
  retention_days: number | null;
  erasures: ErasureReceipt[];
  redactable_rows: number;
}

/** A collection as it appears on a document chip. */
export interface CollectionRef {
  id: string;
  name: string;
  slug: string;
  color: string | null;
}

export interface TagRef {
  id: string;
  name: string;
  color: string | null;
}

/** `GET /v1/collections`. `document_count` is a JOIN on the server, never a counter. */
export interface CollectionSummary extends CollectionRef {
  description: string | null;
  created_at: string | null;
  /** Present only when archived. Archive is not delete: the membership rows survive. */
  archived_at: string | null;
  document_count: number;
}

export interface TagSummary extends TagRef {
  created_at: string | null;
  document_count: number;
}

/** `shared` is `user_id IS NULL` on the server: a view with no owner is everyone's. */
export interface SavedView {
  id: string;
  name: string;
  query: string;
  shared: boolean;
  created_at: string | null;
}

export interface BulkFailure {
  id: string;
  error: string;
}

/** Always a 200. Partial failure is reported here, never swallowed. */
export interface BulkResult {
  affected: number;
  failed: BulkFailure[];
}

/* Templates and fills ------------------------------------------------------- */

/** One answer column or one section/row of a form. Mirrors the server's structure. */
export interface TemplateColumn {
  key: string;
  label: string;
  answerable?: boolean;
}

export interface TemplateRow {
  key: string;
  requirement: string;
  /** Defaults to true server-side; a structural row ("OR", a heading) is false. */
  answerable?: boolean;
}

export interface TemplateSection {
  key: string;
  title: string;
  preamble?: string | null;
  rows: TemplateRow[];
}

/** A value the documents cannot supply — a class threshold, an index number. */
export interface TemplateVariable {
  key: string;
  label: string;
  value?: string | null;
}

export interface TemplateStructure {
  columns: TemplateColumn[];
  sections: TemplateSection[];
  variables?: TemplateVariable[];
}

export interface TemplateSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  created_at: string | null;
  archived_at: string | null;
  /** Null until a structure has been stored. A template with none cannot be filled. */
  latest_version: number | null;
}

export interface TemplateVersionSummary {
  id: string;
  template_id: string;
  version: number;
  /** `extracted` (a machine proposed it, unreviewed), `edited`, or `authored`. */
  origin: string;
  source_document_id: string | null;
  created_at: string | null;
  structure: TemplateStructure;
}

export interface FillCitation {
  document_id: string;
  chunk_id?: string | null;
  quote?: string | null;
}

/** The three-way cell, carried through to the console unflattened. */
export interface FillCell {
  section: string;
  row: string;
  column: string;
  state: "answered" | "not_found" | "conflicted";
  text: string | null;
  reason: string | null;
  citations: FillCitation[];
  drafted_by: string | null;
  confirmed_by: string | null;
}

export interface FillSummary {
  id: string;
  template_version_id: string;
  collection_id: string;
  title: string | null;
  status: "draft" | "drafting" | "drafted" | "failed";
  error: string | null;
  created_at: string | null;
  drafted_at: string | null;
}

export interface FillDetailResponse {
  fill: FillSummary;
  /** The pinned version's structure. Null only if the version was deleted underneath it. */
  structure: TemplateStructure | null;
  cells: FillCell[];
  /** The denominator: answerable cells in the pinned template version. */
  answerable: number;
  answered: number;
  not_found: number;
  conflicted: number;
}

export interface FillMessage {
  id: string;
  role: "user" | "assistant";
  body: string;
  /** Cell addresses this turn changed, so the table can highlight them. */
  touched: string[];
  author: string;
  created_at: string | null;
}
